import { FormControlLabel, Radio, RadioGroup, Stack } from "@mui/material";
import type { DateTimeValidationError } from "@mui/x-date-pickers";
import React, { useMemo } from "react";
import dayjs from "dayjs";
import type { IDatePresetRangeFilterValue } from "Common/Interfaces";
import { EnhancedDateTimePicker } from "../EnhancedDatePicker";
import { DATE_PRESET_OPTIONS } from "./Constants";
import { isDateRangeValueValid } from "./Functions";

const getStartDateErrorMessage = (errorStart: DateTimeValidationError | null, endDate?: dayjs.Dayjs | null) => {
  switch (errorStart) {
    case "maxDate":
      return endDate?.isValid() ? "Start date cannot be after end date" : "Invalid date";
    case "minDate":
      return "Invalid date";
    case "invalidDate":
      return "Invalid date";
    default:
      return "";
  }
};

const getEndDateErrorMessage = (errorEnd: DateTimeValidationError | null, startDate?: dayjs.Dayjs | null) => {
  switch (errorEnd) {
    case "maxDate":
      return "Invalid date";
    case "minDate":
      return startDate?.isValid() ? "End date cannot be before start date" : "Invalid date";
    case "invalidDate":
      return "Invalid date";
    default:
      return "";
  }
};

interface IFilterDatePresetRangePicker {
  value: IDatePresetRangeFilterValue;
  onChangePreset: (preset: string | null) => void;
  onChangeCustomDate: (date: dayjs.Dayjs | null, position: "customStartDate" | "customEndDate") => void;
  onValidationChange?: (isValid: boolean) => void;
}

export const FilterDatePresetRangePicker = (props: IFilterDatePresetRangePicker) => {
  const { value, onChangePreset, onChangeCustomDate, onValidationChange } = props;

  const [errorStart, setErrorStart] = React.useState<DateTimeValidationError | null>(null);
  const [errorEnd, setErrorEnd] = React.useState<DateTimeValidationError | null>(null);

  const isCustomSelected = value.selectedPreset === "custom";

  const isRangeValid = useMemo(() => {
    if (!isCustomSelected) return true;
    if (errorStart || errorEnd) return false;
    return isDateRangeValueValid(value.customStartDate, value.customEndDate);
  }, [errorEnd, errorStart, isCustomSelected, value.customEndDate, value.customStartDate]);

  React.useEffect(() => {
    onValidationChange?.(isRangeValid);
  }, [isRangeValid, onValidationChange]);

  const errorStartMessage = useMemo(() => {
    if (!isCustomSelected) return "";
    return getStartDateErrorMessage(errorStart, value.customEndDate);
  }, [errorStart, isCustomSelected, value.customEndDate]);

  const errorEndMessage = useMemo(() => {
    if (!isCustomSelected) return "";
    return getEndDateErrorMessage(errorEnd, value.customStartDate);
  }, [errorEnd, isCustomSelected, value.customStartDate]);

  return (
    <Stack spacing={1.5}>
      <RadioGroup value={value.selectedPreset ?? ""} onChange={(e) => onChangePreset(e.target.value || null)}>
        {DATE_PRESET_OPTIONS.map((preset) => (
          <FormControlLabel key={preset.value} value={preset.value} control={<Radio size="small" />} label={preset.label} />
        ))}
      </RadioGroup>

      {value.selectedPreset === "custom" && (
        <Stack spacing={2}>
          <EnhancedDateTimePicker
            label="From Date & Time"
            value={value.customStartDate}
            onChange={(newValue) => onChangeCustomDate(newValue, "customStartDate")}
            clearable
            maxDate={value.customEndDate ?? undefined}
            onError={(newError) => setErrorStart(newError)}
            error={errorStartMessage !== ""}
            helperText={errorStartMessage}
          />
          <EnhancedDateTimePicker
            label="To Date & Time"
            value={value.customEndDate}
            onChange={(newValue) => onChangeCustomDate(newValue, "customEndDate")}
            clearable
            minDate={value.customStartDate ?? undefined}
            onError={(newError) => setErrorEnd(newError)}
            error={errorEndMessage !== ""}
            helperText={errorEndMessage}
          />
        </Stack>
      )}
    </Stack>
  );
};
